// ConversationCreateModal.jsx

import React, { useCallback, useState } from 'react';

// Context
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Components
import { getOrCreateConversation } from 'channels/services/ChannelWriteService';
import MemberPickerModal from 'components/memberpicker/MemberPickerModal';

/**
 * ConversationCreateModal
 *
 * Opens the member picker and find-or-creates a conversation on confirm.
 *
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Called when the modal closes.
 * @param {function} [onCreated] - Called after a conversation is opened/created successfully.
 * @returns {ReactNode} The rendered modal.
 */
const ConversationCreateModal = React.memo(({ isOpen, onClose, onCreated }) => {

  // ----- CONTEXT ----- //

  const { user } = useUser();
  const { activeBase } = useBase();

  // ----- ROUTING ----- //

  const navigate = useNavigate();

  // ----- STATE ----- //

  const [errorMessage, setErrorMessage] = useState('');
  const [isOpening, setIsOpening] = useState(false);

  const baseKey = activeBase?.key || null;

  // ----- HANDLERS ----- //

  /**
   * Handles close.
   */

  const handleClose = useCallback(() => {
    if (isOpening) return;
    setErrorMessage('');
    onClose?.();
  }, [isOpening, onClose]);

  /**
   * Handles create conversation.
   *
   * @param {*} selectedPeople
   */
  const handleCreateConversation = useCallback(async (selectedPeople) => {
    if (!baseKey || !user?.key || isOpening) return;

    setErrorMessage('');
    setIsOpening(true);

    try {
      const result = await getOrCreateConversation({
        baseKey,
        userKey: user.key,
        userName: user.name || user.title || user.displayName || user.email || 'Member',
        selectedMemberKeys: selectedPeople,
      });

      const channelKey = result?.channel?.key;
      if (!channelKey) {
        setErrorMessage('Could not open conversation. Try again.');
        return;
      }

      onClose?.();
      onCreated?.();
      navigate(`/channels/${channelKey}`);
    } catch (error) {
      setErrorMessage(error?.message || 'Could not open conversation. Try again.');
    } finally {
      setIsOpening(false);
    }
  }, [isOpening, navigate, onClose, onCreated, user, baseKey]);

  // ----- RENDER ----- //

  return (
    <MemberPickerModal
      baseKey={baseKey}
      label={isOpening ? 'Opening conversation…' : 'Create Message'}
      value={[]}
      isOpen={isOpen}
      setOpen={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
      confirmEnabled={(selectedPeople) => Array.isArray(selectedPeople) && selectedPeople.length > 0}
      confirmLabel={isOpening ? 'Opening conversation…' : 'Open Conversation'}
      isSaving={isOpening}
      submitErrorMessage={errorMessage}
      onChange={() => { }}
      onChangeAsync={handleCreateConversation}
    />
  );
});

// Dev Tools
ConversationCreateModal.displayName = 'ConversationCreateModal';

export default ConversationCreateModal;
